import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar
} from '@mui/material'
import SEO from '@components/SEO'
import { useBetaMode } from '@hooks/useBetaMode'
import {
  Inventory as InventoryIcon,
  Star as StarIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material'

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const { beta } = useBetaMode()

  const features = [
    {
      icon: <InventoryIcon sx={{ fontSize: 60, color: '#DAA520' }} />,
      title: 'Collection Management',
      description: 'Organize and track your cigars across multiple humidors with detailed specifications and inventory management.'
    },
    {
      icon: <StarIcon sx={{ fontSize: 60, color: '#DAA520' }} />,
      title: 'Smoking Journal',
      description: 'Record detailed tasting notes, ratings, and experiences with each cigar you smoke.'
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 60, color: '#DAA520' }} />,
      title: 'Analytics & Insights',
      description: 'Track session analytics and your current collection value from purchase prices.'
    }
  ]

  const testimonials = [
    {
      name: 'Alexander M.',
      role: 'Cigar Collector',
      avatar: 'AM',
      content: 'CigarTrack has revolutionized the way I manage my collection. It\'s simple and effective!',
      rating: 5
    },
    {
      name: 'James K.',
      role: 'Free plan user',
      avatar: 'JK',
      content: 'The analytics feature is my favorite. It helps me understand my preferences better.',
      rating: 5
    },
    {
      name: 'Maria R.',
      role: 'Cigar Enthusiast',
      avatar: 'MR',
      content: 'The smoking journal and analytics help me understand what I enjoy most.',
      rating: 5
    }
  ]

  const pricingPlans = [
    {
      name: 'Free',
      price: 'Free',
      period: '',
      description: 'Available today for casual enthusiasts.',
      features: [
        'Up to 1 humidor',
        '100 cigar limit',
        'Session analytics',
        'Mobile-friendly web app',
        'CSV/JSON export'
      ],
      cta: 'Get Started Free',
      available: true,
      popular: false
    },
    {
      name: 'Premium',
      price: '$9.99',
      period: '/month',
      description: 'Planned — join the waitlist on the Pricing page.',
      features: [
        'Unlimited humidors (planned)',
        'Unlimited cigars (planned)',
        'Advanced analytics (planned)',
        'Priority support (planned)',
        'Data export (available on Free today)'
      ],
      cta: 'Join Waitlist',
      available: false,
      popular: true
    },
    {
      name: 'Pro',
      price: '$19.99',
      period: '/month',
      description: 'Planned — for professionals and power users.',
      features: [
        'Everything in Premium (planned)',
        'API access (planned)',
        'Custom reports (planned)',
        'Dedicated support (planned)'
      ],
      cta: 'Join Waitlist',
      available: false,
      popular: false
    }
  ]

  return (
    <Box>
      <SEO
        title="CigarTrack - The Ultimate Cigar Collection Management Platform"
        description={beta
          ? 'Manage your cigar collection with CigarTrack. Track cigars across humidors, record smoking sessions, and gain insights. Free during beta.'
          : 'Manage your cigar collection with CigarTrack. Track cigars across multiple humidors, record smoking sessions, and gain insights. Free plan available; paid plans coming soon.'}
        keywords="cigar collection, humidor management, cigar tracking, smoking journal, cigar database, cigar app"
        url="/"
      />
      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          py: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 0 }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: '#2F1B14',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.2
              }}
            >
              Track Every Smoke.<br />Master Your Collection.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#5C3D2E',
                mb: 4,
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              The premium cigar management platform for enthusiasts who take their collection seriously. Log sessions, manage humidors, and discover your perfect smoke.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                backgroundColor: '#2F1B14',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 2,
                mb: 4,
                '&:hover': {
                  backgroundColor: '#1a0f0a'
                }
              }}
            >
              Start Free Trial
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Box
              component="img"
              src="/hero-cigar.png"
              alt="CigarTrack — premium cigar collection management"
              sx={{
                maxWidth: 800,
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(62, 31, 0, 0.35)'
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#2F1B14', fontWeight: 600, mb: 6 }}
          >
            Features
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 4,
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#2F1B14', fontWeight: 600, mb: 2 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: 8, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Testimonials
          </Typography>
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  p: 3, 
                  backgroundColor: '#FFFFFF',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <StarIcon key={i} sx={{ color: '#FFD700', fontSize: 20 }} />
                      ))}
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', color: '#2F1B14' }}>
                      &quot;{testimonial.content}&quot;
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: '#8B4513', mr: 2, width: 40, height: 40 }}>
                        {testimonial.avatar}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary">
                        - {testimonial.name}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: 8, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Pricing
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.filter((plan) => !beta || plan.name === 'Free').map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom sx={{ color: '#2F1B14', fontWeight: 600, mb: 2 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {plan.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h4" component="span" sx={{ color: '#2F1B14', fontWeight: 700 }}>
                        {plan.price}
                      </Typography>
                      <Typography variant="h6" component="span" color="text.secondary">
                        {plan.period}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() =>
                        navigate(
                          plan.available
                            ? '/register'
                            : `/pricing`
                        )
                      }
                      sx={{
                        backgroundColor: '#8B4513',
                        color: 'white',
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: '#7A3D0F'
                        }
                      }}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                backgroundColor: 'white',
                color: '#8B4513',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Start Free Trial
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default HomePage
